// form
import { useI18n, useAuth } from 'iomart-hooks';
import { NumericFormat, NumericFormatProps } from 'react-number-format';
import { useFormContext, Controller } from 'react-hook-form';
// @mui
import { InputAdornment, TextField, TextFieldProps } from '@mui/material';
import React from 'react';

// ----------------------------------------------------------------------

type Props = TextFieldProps & {
  name: string;
  currency?: 'RUB' | 'EUR' | 'USD' | 'CNY';
  decimalScale?: number;
  /** false = показывать до decimalScale знаков без доп. нулей (напр. 10.5 или 10.1234) */
  fixedDecimalScale?: boolean;
  /** Вызывается после onChange (для синхронизации связанных полей) */
  onAfterChange?: (value: number) => void;
};

interface CustomProps {
  onChange: (event: { target: { name: string; value: number } }) => void;
  name: string;
}

const currencies = {
  RUB: '₽',
  EUR: '€',
  USD: '$',
  CNY: '¥'
};

const VALID_CURRENCIES = new Set(['RUB', 'EUR', 'USD', 'CNY']);

function normalizeCurrencyCode(c: unknown): 'RUB' | 'EUR' | 'USD' | 'CNY' | null {
  if (c == null || String(c).trim() === '') return null;
  const u = String(c).trim().toUpperCase();
  return VALID_CURRENCIES.has(u) ? (u as 'RUB' | 'EUR' | 'USD' | 'CNY') : null;
}

interface NumericFormatCustomProps extends CustomProps {
  decimalScale?: number;
  fixedDecimalScale?: boolean;
}

const NumericFormatCustom = React.forwardRef<NumericFormatProps, NumericFormatCustomProps>(
  function NumericFormatCustom(props, ref) {
    const { onChange, decimalScale = 2, fixedDecimalScale = true, ...other } = props;
    return (
      <NumericFormat
        {...other}
        getInputRef={ref}
        onValueChange={(values) => {
          onChange({
            target: {
              name: props.name,
              value: values.floatValue || 0,
            },
          });
        }}
        thousandSeparator=" "
        decimalSeparator="."
        allowedDecimalSeparators={[',', '.']}
        prefix=""
        decimalScale={decimalScale}
        fixedDecimalScale={fixedDecimalScale}
        allowNegative={false}
      />
    );
  }
);

export default function RHFMoney({
  name,
  label,
  currency,
  decimalScale = 2,
  fixedDecimalScale = true,
  helperText,
  onAfterChange,
  ...other
}: Props) {
  const { control } = useFormContext();
  const { t } = useI18n(false);
  const { mainCurrency } = useAuth(false) as any;
  const resolvedCurrency =
    normalizeCurrencyCode(currency) ??
    normalizeCurrencyCode(mainCurrency) ??
    'RUB';
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={null}
      render={({ field, fieldState: { error } }) => {
        const { ref, ...restField } = field;
        return (
          <TextField
            {...restField}
            inputRef={ref}
            onChange={(e: any) => {
            field.onChange(e);
            onAfterChange?.(Number((e?.target as HTMLInputElement)?.value) || 0);
          }}
          label={t(label)}
          fullWidth
          value={field.value || field.value === 0 ? field.value : ''}
          error={!!error}
          helperText={error ? error?.message : helperText}
          InputProps={{
            inputComponent: NumericFormatCustom as any,
            inputProps: { decimalScale, fixedDecimalScale },
            slotProps: { input: { decimalScale, fixedDecimalScale } } as any,
            startAdornment: (
              <InputAdornment position="start" sx={{ pt: 0.3 }}>
                {currencies[resolvedCurrency] || resolvedCurrency}
              </InputAdornment>
            )
          }}
          {...other}
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={{
            ...other.sx,
            '& .MuiInputBase-input': {
              font: 'menu'
            }
          }}
          />
        );
      }}
    />
  );
}
